import { booleanAttr } from "@wiki/core";
import { setBlockType } from "prosemirror-commands";
import { TextSelection, type Command, type EditorState } from "prosemirror-state";

import { findParentOfType, nodes, type FoundNode } from "../schema";

const INDENT = "  ";

function codeBlockAt(state: EditorState): FoundNode | null {
  return findParentOfType(state.selection.$from, nodes.codeBlock);
}

/** `Enter` inside a code block inserts a newline and never splits it (§10). */
export const newlineInCodeBlock: Command = (state, dispatch) => {
  if (!codeBlockAt(state)) return false;
  if (dispatch) dispatch(state.tr.insertText("\n").scrollIntoView());
  return true;
};

/** `Mod-A` selects the block's own text before the whole document. */
export const selectCodeBlock: Command = (state, dispatch) => {
  const found = codeBlockAt(state);
  if (!found) return false;
  const from = found.pos + 1;
  const to = found.pos + found.node.nodeSize - 1;
  if (state.selection.from === from && state.selection.to === to) return false;
  if (dispatch) dispatch(state.tr.setSelection(TextSelection.create(state.doc, from, to)));
  return true;
};

/** The offsets of the starts of every line the selection touches. */
function selectedLineStarts(state: EditorState, found: FoundNode): number[] {
  const start = found.pos + 1;
  const text = found.node.textContent;
  const from = Math.max(state.selection.from, start) - start;
  const to = Math.max(state.selection.to, start) - start;
  const starts: number[] = [];
  let lineStart = 0;
  for (const line of text.split("\n")) {
    const lineEnd = lineStart + line.length;
    if (lineStart <= to && lineEnd >= from) starts.push(lineStart);
    lineStart = lineEnd + 1;
  }
  return starts.map((offset) => start + offset);
}

/** `Mod-]` / `Tab` inside a code block. */
export const indentCodeBlock: Command = (state, dispatch) => {
  const found = codeBlockAt(state);
  if (!found) return false;
  if (dispatch) {
    const tr = state.tr;
    for (const pos of selectedLineStarts(state, found).reverse()) {
      tr.insertText(INDENT, tr.mapping.map(pos));
    }
    dispatch(tr.scrollIntoView());
  }
  return true;
};

/** `Mod-[` / `⇧-Tab` inside a code block. */
export const outdentCodeBlock: Command = (state, dispatch) => {
  const found = codeBlockAt(state);
  if (!found) return false;
  const start = found.pos + 1;
  const text = found.node.textContent;
  const removals: { from: number; to: number }[] = [];
  for (const pos of selectedLineStarts(state, found)) {
    const offset = pos - start;
    const line = text.slice(offset, offset + INDENT.length);
    const width = line.startsWith(INDENT) ? INDENT.length : line.startsWith(" ") ? 1 : 0;
    if (width > 0) removals.push({ from: pos, to: pos + width });
  }
  if (removals.length === 0) return false;
  if (dispatch) {
    const tr = state.tr;
    for (const range of removals.reverse()) tr.delete(range.from, range.to);
    dispatch(tr.scrollIntoView());
  }
  return true;
};

/** `Backspace` at the start of a code block turns it back into a paragraph. */
export const exitCodeBlockOnBackspace: Command = (state, dispatch, view) => {
  const found = codeBlockAt(state);
  if (!found) return false;
  if (!state.selection.empty || state.selection.from !== found.pos + 1) return false;
  return setBlockType(nodes.paragraph)(state, dispatch, view);
};

export function setCodeLanguage(pos: number, language: string): Command {
  return (state, dispatch) => {
    const node = state.doc.nodeAt(pos);
    if (node?.type !== nodes.codeBlock) return false;
    if (dispatch) dispatch(state.tr.setNodeAttribute(pos, "language", language));
    return true;
  };
}

export function toggleCodeWrap(pos: number): Command {
  return (state, dispatch) => {
    const node = state.doc.nodeAt(pos);
    if (node?.type !== nodes.codeBlock) return false;
    if (dispatch) dispatch(state.tr.setNodeAttribute(pos, "wrap", !booleanAttr(node, "wrap")));
    return true;
  };
}

/**
 * The picker's curated list (`editor.md` §5, open question 5). A block whose
 * language is not listed keeps it: the dialect preserves unknown languages
 * verbatim, so the picker adds the current value rather than replacing it.
 */
export const CODE_LANGUAGES: readonly { value: string; label: string }[] = [
  { value: "", label: "Plain text" },
  { value: "bash", label: "Bash" },
  { value: "c", label: "C" },
  { value: "cpp", label: "C++" },
  { value: "csharp", label: "C#" },
  { value: "css", label: "CSS" },
  { value: "diff", label: "Diff" },
  { value: "docker", label: "Docker" },
  { value: "elixir", label: "Elixir" },
  { value: "erlang", label: "Erlang" },
  { value: "go", label: "Go" },
  { value: "graphql", label: "GraphQL" },
  { value: "html", label: "HTML" },
  { value: "java", label: "Java" },
  { value: "javascript", label: "JavaScript" },
  { value: "json", label: "JSON" },
  { value: "kotlin", label: "Kotlin" },
  { value: "lua", label: "Lua" },
  { value: "markdown", label: "Markdown" },
  { value: "mermaid", label: "Mermaid" },
  { value: "nix", label: "Nix" },
  { value: "objectivec", label: "Objective-C" },
  { value: "ocaml", label: "OCaml" },
  { value: "perl", label: "Perl" },
  { value: "php", label: "PHP" },
  { value: "powershell", label: "PowerShell" },
  { value: "protobuf", label: "Protobuf" },
  { value: "python", label: "Python" },
  { value: "r", label: "R" },
  { value: "ruby", label: "Ruby" },
  { value: "rust", label: "Rust" },
  { value: "scala", label: "Scala" },
  { value: "solidity", label: "Solidity" },
  { value: "sql", label: "SQL" },
  { value: "swift", label: "Swift" },
  { value: "toml", label: "TOML" },
  { value: "typescript", label: "TypeScript" },
  { value: "xml", label: "XML" },
  { value: "yaml", label: "YAML" },
  { value: "zig", label: "Zig" },
];

export function isKnownLanguage(language: string): boolean {
  return CODE_LANGUAGES.some((entry) => entry.value === language);
}
